import { useEffect, useMemo } from 'react';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { AppName } from 'sl-api-connector';
import { useBeaconHomeLink } from 'src/components/BeaconV2Components/hooks/useBeaconHomeLink';
import { BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { BEACON_ADVERTISING_DEPRECATION_SPLIT, getAdManagerUrl, getAppName } from 'src/utils/app';
import { useAppSelector, useHistory, useLocation } from 'src/utils/Hooks';

/**
 * Opens Ad Manager in a new browser tab. Popup blockers refuse `window.open`
 * calls that are not tied to a user gesture (e.g. when the user typed a
 * `tab=advertising` URL), in which case we fall back to navigating this tab so
 * the user still lands in Ad Manager rather than on the deprecated page.
 */
export const openAdManager = (adManagerUrl: string) => {
  const newWindow = window.open(adManagerUrl, '_blank', 'noopener,noreferrer');
  if (!newWindow) {
    window.location.assign(adManagerUrl);
  }
};

/**
 * Reads the `beacon-advertising-deprecation` Split.io flag for the current user.
 *
 * Uses the Split React hook (rather than `splitFactory.client().getTreatment`)
 * so the value re-renders when the SDK becomes ready — Beacon does not block
 * rendering on Split readiness, and a one-shot `control` read on URL entry
 * would otherwise leave the deprecated page visible until the next navigation.
 */
export const useBeaconAdvertisingDeprecation = () => {
  const userEmail = useAppSelector((state) => state.user?.session?.email ?? '');
  const retailerId = useAppSelector((state) => state.retailer?.id ?? '');
  const beaconClientId = useAppSelector((state) => state.user?.config?.vendor?.BeaconClientId ?? '');

  const attributes = useMemo(
    () => ({
      userEmail,
      retailerId: String(retailerId),
      beaconClientId: String(beaconClientId),
      host: window.location.hostname
    }),
    [userEmail, retailerId, beaconClientId]
  );

  const { treatments } = useSplitTreatments({ names: [BEACON_ADVERTISING_DEPRECATION_SPLIT], attributes });

  const isDeprecated =
    getAppName() === AppName.Beacon && treatments[BEACON_ADVERTISING_DEPRECATION_SPLIT]?.treatment === 'on';

  return { isDeprecated, adManagerUrl: getAdManagerUrl() };
};

/**
 * While the deprecation flag is on, any Beacon URL carrying `tab=advertising`
 * (typed directly, bookmarked, or reached through an in-app link such as the
 * sidebar "Rules" quick link) opens Ad Manager in a new tab and replaces the
 * current entry with Beacon home so the deprecated page never renders and the
 * back button does not loop into the redirect again.
 *
 * Mount once on every Beacon page; `PrimaryNavBar` is the natural host because
 * it is always rendered there and owns the Advertising tab itself.
 */
export const useBeaconAdvertisingDeprecationRedirect = () => {
  const history = useHistory();
  const location = useLocation();
  const beaconHomeLink = useBeaconHomeLink();
  const { isDeprecated, adManagerUrl } = useBeaconAdvertisingDeprecation();

  const isAdvertisingTab = useMemo(
    () => new URLSearchParams(location.search).get('tab') === BEACON_TABS.ADVERTISING,
    [location.search]
  );

  useEffect(() => {
    if (!isDeprecated || !isAdvertisingTab) {
      return;
    }
    history.replace(beaconHomeLink);
    openAdManager(adManagerUrl);
  }, [isDeprecated, isAdvertisingTab, adManagerUrl, beaconHomeLink, history]);

  return { isDeprecated, adManagerUrl };
};
